<template>
    <div class="users" v-can="'user.access'" v-if="isMounted">
        <ds-filter
            @resultFilter="resultFilter"
            :data="users"
            :fields="filterFields"
        ></ds-filter>

        <ds-table
            :searchKeys="searchKeys"
            spacer
            :data="usersFilter"
            :heads="getTableHeader"
            :max-items="10"
            :view-items="[10, 15, 20]"
            search
        >
            <template v-slot:header>
                <vs-button
                    v-can="'user.create'"
                    @click="createUser"
                    class="mr-4 whitespace-no-wrap"
                    color="primary"
                    type="border"
                    v-bind="$iconData('icon-plus', 'fi-rr-plus')"
                >
                    {{ $t('buttons.Create_user') }}
                </vs-button>
            </template>

            <template v-slot:id="{id}">
                <div class="flex items-center">
                    <div v-if="id.user_id" class="ml-2">{{ id.user_id }}</div>
                    <div v-else>-</div>
                </div>
            </template>

            <template v-slot:user="{user}">
                <div>
                    <div class="flex items-center">
                        <vs-icon color="primary" v-bind="$iconData('icon-user', 'fi-rr-user')"></vs-icon>
                        <div class="ml-2">{{ user.name }}</div>
                    </div>
                    <div class="flex items-center">
                        <div class="text-xs camp-ident">{{ user.hash }}</div>
                        <a class="ml-2 flex" v-tooltip="$t('tooltip.Copy')" @click.stop.prevent="copyHash(user.hash)" href="#">
                            <vs-icon class="mr-1" v-bind="$iconData('icon-copy', 'fi-rr-copy-alt')"/>
                        </a>
                    </div>
                </div>
            </template>

            <template v-slot:email="{email}">
                <div class="flex items-center">
                    <vs-icon color="primary" v-bind="$iconData('icon-mail', 'fi-rr-envelope')"></vs-icon>
                    <div class="ml-2">{{ email.email }}</div>
                </div>
            </template>

            <template v-slot:team="{team}">
                <div class="flex items-center" v-if="isPermission('settings_team.show_team_name')">
                    <vs-button
                        v-can="'settings_team.access'"
                        radius
                        color="primary"
                        type="flat"
                        v-bind="$iconData('icon-users', 'fi-rr-users')"
                        :to="`/settings/teams?team=${toString(team.team_name)}`"
                    ></vs-button>
                    <div class="ml-2">{{ team.team_name }}</div>
                </div>
                <div v-else>{{team.team_id}}</div>
            </template>

            <template v-slot:role="{role}">
                <div class="flex items-center">
                    <div>{{ role.role_name }}</div>
                </div>
            </template>

            <template v-slot:status="{status}">
                <vs-switch v-can="'user.destroy'" color="success" :checked="status.is_active"
                           @click="changeStatus(status)" icon-pack="feather" vs-icon-off="icon-x"
                           vs-icon-on="icon-check"/>
            </template>

            <template v-slot:action="{action}">
                <div class="flex items-center">
                    <vs-button v-can="'user.update'" radius color="primary" type="flat" v-bind="$iconData('icon-edit-2', 'fi-rr-file-edit')" @click="editUser(action)"
                               v-tooltip="$t('modals.Edit_user')"></vs-button>
                    <vs-button v-can="'user.permission'" radius color="primary" type="flat" v-bind="$iconData('icon-terminal', 'fi-rr-square-terminal')" @click="editUserPermission(action.hash)"
                               v-tooltip="$t('modals.Edit_user_permission')"></vs-button>
                    <vs-button v-can="'user.password'" radius color="primary" type="flat" v-bind="$iconData('icon-lock', 'fi-rr-lock')" @click="updatePass(action.hash)"
                               v-tooltip="$t('modals.Update_password')"></vs-button>
                    <vs-button v-can="'user.autologin'" :disabled="!~~action.is_active" radius color="primary"
                               type="flat" v-bind="$iconData('icon-log-in', 'fi-rr-enter')" @click="autologin(action.hash)"
                               v-tooltip="$t('tooltip.Login_as_this_user')"></vs-button>
                </div>
            </template>
        </ds-table>
    </div>
</template>

<script>
import ErrorHandler from "@/utils/errorHandler"
import send from "@/utils/fetch"
import {copy} from "@/utils/utils"
import EventBus from "@/store/eventBus"
import {checkPermission, dataSort, vSelectPosition, utils} from '@/utils/mixins'

export default {
    mixins: [checkPermission, dataSort, vSelectPosition, utils],

    data() {
        return {
            isMounted: false,
            usersList: [],
            usersListFilter: [],
            searchKeys: ['team_name', 'role_name', 'hash'],
            filterFields: [
                {name: 'table_filters.active_name', type: 'select', label: 'active_name', field: 'active_name'},
                {name: 'table_filters.team_name', type: 'select', label: 'team_name', field: 'team_id'},
                {name: 'table_filters.role_name', type: 'select', label: 'role_name', field: 'role_id'},
            ],
            heads: [
                {name: 'global.id', key: 'id', sortKey: 'user_id', value: 'id', display: true, can: 'user.show_user_id'},
                {name: 'global.Name', key: 'name', sortKey: 'name', value: 'user', display: true},
                {name: 'global.Email', key: 'email', sortKey: 'email', value: 'email', display: true},
                {name: 'global.Team', key: 'team', sortKey: 'team_name', value: 'team', display: true},
                {name: 'global.Role', key: 'role', sortKey: 'role_name', value: 'role', display: true},
                {name: 'global.Status', key: 'status', sortKey: 'is_active', value: 'status', display: true},
                {name: 'global.Actions', key: 'action', value: 'action', display: true}
            ]
        }
    },

    computed: {
        users() {
            return this.usersList
        },

        usersFilter() {
            return this.usersListFilter.length ? this.usersListFilter : this.users
        },

        getTableHeader() {
            let headers = [...this.heads]
            if (!this.isPermission('user.update') && !this.isPermission('user.password') && !this.isPermission('user.autologin')) {
                headers = this.changeVisibilityOfTableHeader(headers, 'action')
            }
            if (!this.isPermission('user.destroy')) {
                headers = this.changeVisibilityOfTableHeader(headers, 'status')
            }
            return headers
        }
    },

    methods: {
        resultFilter(data) {
            this.usersListFilter = data
        },

        toString(string) {
            return string.split(' ').join('%')
        },

        changeVisibilityOfTableHeader(arrayHeaders, column) {
            return arrayHeaders.map(head => {
                if (head.value === column) {
                    return {
                        ...head,
                        display: false
                    }
                } else return head
            })
        },

        copyHash(hash) {
            copy(hash)

            this.$vs.notify({
                title: this.$t('global.Copy'),
                text: hash,
                color: 'success',
                iconPack: 'feather',
                icon: 'icon-check'
            })
        },

        createUser() {
            EventBus.$emit('emitPopupEvent', {type: 'createUser', name: this.$t('buttons.Create_user')})
        },

        editUser(data) {
            EventBus.$emit('emitPopupEvent', {type: 'editUser', name: this.$t('modals.Edit_user'), data})
        },

        editUserPermission(hash) {
            EventBus.$emit('emitPopupEvent', {
                type: 'editUserPermission',
                name: this.$t('modals.Edit_user_permission'),
                data: {hash}
            })
        },

        updatePass(hash) {
            EventBus.$emit('emitPopupEvent', {
                type: 'updateUserPass',
                name: this.$t('modals.Update_password'),
                ids: {hash}
            })
        },

        async autologin(hash) {
            try {
                await this.$store.dispatch('autologin', hash)
            } catch (e) {
                new ErrorHandler(e).send
            }
        },

        changeStatus(data) {
            this.$vs.dialog({
                type: 'confirm',
                color: 'warning',
                title: this.$t('confirm.Confirm_action'),
                text: data.is_active ? this.$t('confirm.deactivateUser') : this.$t('confirm.activateUser'),
                accept: async () => {
                    await send(this.$url.user.destroy, 'DELETE', {
                        data: {hash: data.hash, status: data.is_active}
                    })

                    await this.get()
                },
                acceptText: data.is_active ? this.$t('buttons.Deactivate') : this.$t('buttons.Activate'),
                cancelText: this.$t('global.Cancel')
            })
        },

        mutationData(data) {
            const users = []
            data.map(user => {
                user.team_name = this.isPermission('settings_team.show_team_name') ? user.team_name : user.team_id
                user.active_name = user.is_active ? 'On' : 'Off'

                users.push(user)
            })
            return users
        },

        async get() {
            try {
                this.usersList = await send(this.$url.user.get, 'GET')
                    .then(response => this.createNameById(response.data.result, false))
                    .then(response => this.mutationData(response))
            } catch (e) {
                new ErrorHandler(e).send
            }
        }
    },

    async mounted() {
        await this.get()

        EventBus.$on('updateUsers', async () => {
            await this.get()
        })

        this.isMounted = true
    },

    beforeDestroy() {
        EventBus.$off('updateUsers')
    }
}
</script>

<style lang="scss">
.users {
    .camp-ident {
        color: #bfbfbf;
    }
}
</style>
